import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { useUserContext } from 'providers/UserProvider';
import useFormValidation from 'hooks/useFormValidation';

import SearchableInput from 'components/common/SearchableInput';

import { inventoryApi, shelfApi, productNameApi } from 'api';
import { INVENTORY_TYPE } from 'constants/inventory';

const validationSchema = {
  product: {
    required: 'Product is required',
  },
  inventoryType: {
    required: 'Inventory category is required',
  },
  shelf: {
    required: 'Shelf is required',
  },
  quantity: {
    required: 'Quantity is required',
    type: 'number',
    invalidNumber: 'Please enter a number',
  },
};

const initialValues = {
  product: '',
  productName: '',
  inventoryType: '',
  shelf: '',
  shelfCode: '',
  quantity: '',
};

const GatePassItemAddModal = ({ show, onHide, onSave }) => {
  const { selectedWarehouse } = useUserContext();

  const [isLoadingInventories, setIsLoadingInventories] = useState(false);
  const [existingInventories, setExistingInventories] = useState({
    count: 0,
    results: [],
  });
  const [selectedProductBatch, setSelectedProductBatch] = useState(null);
  const [availableQuantity, setAvailableQuantity] = useState(0);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    resetForm,
    isValid,
  } = useFormValidation(initialValues, validationSchema);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchExistingInventories = async () => {
      if (!values.product) {
        if (isMounted) {
          setExistingInventories({ count: 0, results: [] });
        }
        return;
      }

      const params = {
        product: Number(values.product),
        page_size: 10,
        warehouse: selectedWarehouse?.id,
        sort: 'expire_date',
      };

      if (values.inventoryType) {
        params.inventory_type = values.inventoryType;
      }

      if (values.shelf) {
        params.shelf = values.shelf;
      }

      if (isMounted) {
        setIsLoadingInventories(true);
      }

      try {
        const response = await inventoryApi.getList(params, { signal });
        if (isMounted) {
          setExistingInventories(response);
        }
      } catch (error) {
        if (
          error.name === 'AbortError' ||
          error.name === 'CanceledError' ||
          error.code === 'ERR_CANCELED'
        ) {
          // Request was cancelled, ignore
          return;
        }
        if (isMounted) {
          toast.error('Failed to fetch existing inventories');
        }
      } finally {
        if (isMounted) {
          setIsLoadingInventories(false);
        }
      }
    };

    fetchExistingInventories();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [
    values.product,
    values.inventoryType,
    values.shelf,
    selectedWarehouse?.id,
  ]);

  const handleProductInputChange = value => {
    // 입력값이 변경되면 product 초기화
    handleChange('product', '');
    handleChange('productName', value);
    setSelectedProductBatch(null);
    setAvailableQuantity(0);
  };

  const handleProductSelect = selectedProduct => {
    handleChange('product', selectedProduct.id);
    handleChange('productName', selectedProduct.name);
    setSelectedProductBatch(null);
    setAvailableQuantity(0);
  };

  const handleShelfInputChange = value => {
    handleChange('shelf', '');
    handleChange('shelfCode', value);
    setSelectedProductBatch(null);
    setAvailableQuantity(0);
  };

  const handleShelfSelect = selectedShelf => {
    handleChange('shelf', selectedShelf.id);
    handleChange('shelfCode', selectedShelf.code);
    setSelectedProductBatch(null);
    setAvailableQuantity(0);
  };

  const handleSelectInventory = inventory => {
    handleChange('shelf', inventory.shelf.id);
    handleChange('shelfCode', inventory.shelf.code);
    handleChange('inventoryType', inventory.inventory_type);
    setSelectedProductBatch(inventory?.id);
    setAvailableQuantity(inventory.quantity);
  };

  const handleAddItem = async () => {
    if (!selectedProductBatch) {
      toast.error('Please select an inventory item.');
      return;
    }
    if (Number(values.quantity) > availableQuantity) {
      toast.error('Quantity cannot be greater than available quantity.');
      return;
    }

    const itemData = {
      product_batch: selectedProductBatch,
      quantity: Number(values.quantity),
    };

    onSave(itemData);
    closeModal();
  };

  const closeModal = () => {
    resetForm();
    setSelectedProductBatch(null);
    setAvailableQuantity(0);
    onHide();
  };

  const renderExistingInventories = () => {
    if (!values.product || isLoadingInventories) return null;

    if (existingInventories.count === 0) return null;

    return (
      <div className="mb-3" style={{ maxHeight: '250px', overflowY: 'auto' }}>
        <h6 className="fw-bold mb-1">
          Exist Inventory List (Total: {existingInventories.count})
        </h6>

        {existingInventories.count > 10 && (
          <p className="fs-11 text-danger mb-1">
            Only showing first 10 inventories
          </p>
        )}

        <Table size="sm">
          <thead>
            <tr className="bg-light fs-10">
              <th className="text-start">Shelf Code</th>
              <th className="text-center">Category</th>
              <th className="text-center">Expiry Date</th>
              <th className="text-end">Total Qty</th>
              <th className="text-end">Available Qty</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {existingInventories.results.map(inventory => (
              <tr
                key={inventory.id}
                className={`fs-10 ${
                  selectedProductBatch === inventory.id ? 'table-success' : ''
                }`}
                style={{ verticalAlign: 'center' }}
              >
                <td className="text-start">{inventory.shelf.code}</td>
                <td className="text-center">
                  {String(inventory.inventory_type || '').toUpperCase()}
                </td>
                <td className="text-center">{inventory.expire_date || '-'}</td>
                <td className="text-end">{inventory.quantity}</td>
                <td className="text-end">
                  {inventory.quantity - inventory.quantity_allocated}
                </td>
                <td>
                  {selectedProductBatch === inventory.id ? (
                    inventory.quantity - inventory.quantity_allocated === 0 ? (
                      <Button
                        size="sm"
                        className="btn-disabled"
                        disabled
                        style={{ minWidth: 90, textAlign: 'center' }}
                      >
                        N/A
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="btn-success"
                        style={{ minWidth: 90, textAlign: 'center' }}
                      >
                        Selected
                      </Button>
                    )
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleSelectInventory(inventory)}
                      style={{ minWidth: 90, textAlign: 'center' }}
                      disabled={
                        inventory.quantity - inventory.quantity_allocated === 0
                      } // 수량 0이면 클릭 불가
                    >
                      {inventory.quantity - inventory.quantity_allocated === 0
                        ? 'N/A'
                        : 'Select'}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={closeModal} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add Gate Pass Item</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {renderExistingInventories()}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Select Product<span className="text-danger">*</span>
            </Form.Label>
            <SearchableInput
              apiService={productNameApi}
              searchField="search__icontains"
              value={values.productName}
              onChange={handleProductInputChange}
              onSelect={handleProductSelect}
              onBlur={() => handleBlur('product')}
              placeholder="Enter 2 or more characters to search products..."
              renderItem={item => (
                <div className="font-medium">{item?.name}</div>
              )}
              visibleItems={2}
              disabled={false}
            />
            {touched.product && errors.product && (
              <Form.Text className="text-danger">{errors.product}</Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Inventory Category<span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={values.inventoryType}
              onChange={e => handleChange('inventoryType', e.target.value)}
              onBlur={() => handleBlur('inventoryType')}
              isInvalid={touched.inventoryType && errors.inventoryType}
            >
              <option value="">Select Inventory Category...</option>
              {INVENTORY_TYPE.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.inventoryType}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Shelf<span className="text-danger">*</span>
            </Form.Label>
            <SearchableInput
              apiService={shelfApi}
              searchField="code__icontains"
              additionalParams={{ warehouse: selectedWarehouse?.id }}
              displayField="code"
              value={values.shelfCode}
              onChange={handleShelfInputChange}
              onSelect={handleShelfSelect}
              onBlur={() => handleBlur('shelf')}
              placeholder="Enter 2 or more characters to search shelf..."
              renderItem={item => (
                <div className="font-medium">{item?.code}</div>
              )}
              visibleItems={2}
              disabled={false}
            />
            {touched.shelf && errors.shelf && (
              <Form.Text className="text-danger">{errors.shelf}</Form.Text>
            )}
          </Form.Group>

          <Form.Group>
            <Form.Label>
              Quantity<span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="input"
              value={values.quantity}
              onChange={e => handleChange('quantity', e.target.value)}
              placeholder="Enter Quantity..."
              onBlur={() => handleBlur('quantity')}
              isInvalid={touched.quantity && errors.quantity}
            />
            <Form.Control.Feedback type="invalid">
              {errors.quantity}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => {
            resetForm();
            setSelectedProductBatch(null);
            setAvailableQuantity(0);
          }}
        >
          Clear
        </Button>
        <Button
          variant="primary"
          onClick={handleAddItem}
          disabled={!isValid || !selectedProductBatch}
        >
          Add
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GatePassItemAddModal;
